import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Trophy,
  Users,
  FileText,
  Package,
  Gift,
  TrendingUp,
  Upload,
  CalendarDays,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  useConfiguracionFidelizacion,
  useFacturasIngreso,
  useMetadataFidelizacion,
  useMetricasPorCampo,
  useMetricasPorProducto,
  useResumenFidelizacion,
  useSerieTemporal,
  useTodasCanjesFidelizacion,
  useTodasFacturasFidelizacion,
  useTodosParticipantesFidelizacion,
} from '../hooks/useFidelizacion'
import { parseFidelizacionError } from '../hooks/useFidelizacionError'
import { normalizeColor } from '../lib/reporteColor'
import { KpiCard } from '../components/KpiCard'
import { ReporteHeader } from '../components/ReporteHeader'
import { ReporteSubNav } from '../components/ReporteSubNav'
import { usePeriodoReporte } from '../hooks/usePeriodoReporte'
import { ReportePeriodoBar } from '../components/ReportePeriodoBar'
import { AreaChartFecha } from '../components/charts/AreaChartFecha'
import { GranularidadToggle } from '../components/GranularidadToggle'
import { agruparSerieFecha, claveGranularidad, GRAN_RITMO, GRAN_SUFIJO, type Granularidad } from '../lib/serieGranularidad'
import { parseNumeric } from '../components/charts/palette'
import { mapaPreciosReporte, precioReferenciaPromedioPonderado, valorProductoReferencia } from '../lib/preciosReferencia'
import { idsProductoDeCategoria, type CategoriaProducto } from '../lib/categoriaProducto'
import { formatMoneda, formatNumero, esPeru, terminoComprobante } from '../lib/paisMapa'
import {
  DynamicFiltersBar,
  type DynamicFiltersValue,
  type FieldOption,
} from '../components/DynamicFiltersBar'
import { useCascadaRepresentante } from '../hooks/useCascadaRepresentante'
import {
  CAMPO_CADENA_CANDIDATES,
  CAMPO_REPRESENTANTE_CANDIDATES,
  autoFieldOptions,
  collectPairs,
  parseMulti,
  participantePairs,
  pickCampoFiltrable,
} from '../lib/campos'
import { TablaEmbudoRepresentante } from '../components/TablaEmbudoRepresentante'
import type { EmbudoRepresentanteFila } from '../types'

// Costo por comprobante validado. Perú (config 7) usa 0.40; el resto 0.10.
const COSTO_UNITARIO_FACTURA = 0.1
const COSTO_UNITARIO_FACTURA_PERU = 0.4


const TZ_PERU = 'America/Lima'
function toLocalDate(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso.slice(0, 10)
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ_PERU, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
}

function formatDecimal(value: string | number | undefined | null, configId: number, decimals = 2): string {
  if (value === null || value === undefined) return '—'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (Number.isNaN(num)) return String(value)
  return formatNumero(num, configId, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

function formatInteger(value: number | undefined | null, configId: number): string {
  if (value === null || value === undefined) return '—'
  return formatNumero(value, configId)
}

export function ReporteDetallePage() {
  const { id } = useParams<{ id: string }>()
  const configId = id ? parseInt(id, 10) : 0

  const config = useConfiguracionFidelizacion(configId)
  const metadata = useMetadataFidelizacion(configId)

  // En modo 'valor' las ventas se miden en $ (monto); en 'cantidad', en cajas.
  const esValor = (metadata.data?.puntos_modo ?? 'cantidad') === 'valor'
  const costoUnitarioFactura = esPeru(configId) ? COSTO_UNITARIO_FACTURA_PERU : COSTO_UNITARIO_FACTURA

  // En reportes con lista de precios local (Medifarma id 5/7, Grupo Farma id 3)
  // el monto del programa (card "total mes" + serie diaria) se valora con esos
  // precios en vez del OCR. `mapaPrecios` es null en el resto de reportes.
  const mapaPrecios = useMemo(
    () => mapaPreciosReporte({ id: configId, slug: config.data?.slug, nombre: config.data?.nombre }),
    [configId, config.data?.slug, config.data?.nombre],
  )
  const usaPreciosLocales = mapaPrecios != null

  // "Todo el histórico": al activarse se omite el rango de fechas y todas las
  // métricas se calculan sobre el total de datos del programa. `modo` alterna
  // entre selector de mes y rango de fechas personalizado.
  const {
    mes,
    setMes,
    sinFecha,
    setSinFecha,
    modo,
    setModo,
    rangoPersonalizado,
    setRangoPersonalizado,
    rango,
    rangoEfectivo,
  } = usePeriodoReporte({ sinFechaInicial: true })

  // Granularidad de las gráficas de serie temporal (Registros y Monto día a día).
  // En vistas agregadas (semana/mes) se amplía el rango a todo el histórico.
  const [granSerie, setGranSerie] = useState<Granularidad>('dia')
  const esAmpliado = granSerie !== 'dia'

  // ---- Filtros de la sección (solo Medifarma Perú, id 7) ----
  // Cascada de fuerza de ventas (Tipo/Jefe/Supervisor/Representante) + Departamento
  // + Cadena + Producto, igual que la sección Ventas. Recalculan las métricas.
  const [filtros, setFiltros] = useState<DynamicFiltersValue>({})
  const camposExternos = metadata.data?.campos_externos
  const campoRepFiltro = useMemo(
    () => pickCampoFiltrable(camposExternos, CAMPO_REPRESENTANTE_CANDIDATES),
    [camposExternos],
  )
  const campoCadenaFiltro = useMemo(
    () => pickCampoFiltrable(camposExternos, CAMPO_CADENA_CANDIDATES),
    [camposExternos],
  )
  // Listado de participantes: base para dropdowns/cascada (id 7) y embudo por
  // representante (cualquier tenant con campo representante).
  const todosParticipantes = useTodosParticipantesFidelizacion(
    configId === 7 || campoRepFiltro != null ? configId : null,
  )
  const { cascadeSelects, inyectarReps, narrowRepOptions } = useCascadaRepresentante({
    configId,
    participantes: todosParticipantes.data,
    repKey: campoRepFiltro?.key,
    filtros,
    setFiltros,
  })

  // Filtros dinámicos hacia el backend (representantes del jefe inyectados si hay
  // cascada). `producto` se excluye: no se envía server-side (rompería el upstream
  // con multi-selección) y se aplica client-side sobre el desglose por producto.
  const filtrosDinamicos = useMemo(() => {
    const enviar: DynamicFiltersValue = { ...inyectarReps(filtros) }
    delete enviar.producto
    delete enviar.categoria
    return enviar
  }, [filtros, inyectarReps])
  // rango + filtros dinámicos: alimenta todas las métricas server-side del Resumen.
  const filtrosCompletos = useMemo(
    () => ({ ...rangoEfectivo, ...filtrosDinamicos }),
    [rangoEfectivo, filtrosDinamicos],
  )
  // Filtros para las series con toggle: en vista agregada se omite el rango para
  // consultar todo el histórico (conservando el resto de filtros dinámicos).
  const filtrosSerie = useMemo(() => {
    if (!esAmpliado) return filtrosCompletos
    const resto: DynamicFiltersValue = { ...filtrosCompletos }
    delete resto.desde
    delete resto.hasta
    return resto
  }, [esAmpliado, filtrosCompletos])
  // Categoría Consumo/Farma seleccionada (solo reporte 7) — client-side.
  const categoriaSel = filtros.categoria as CategoriaProducto | undefined

  // ---- Embudo por representante (tenants con campo representante) ----
  // Activos del periodo: participantes únicos con ≥1 factura (campo `participantes`
  // del endpoint agrupación con fechas = exactamente esa definición).
  const porCampoRep = useMetricasPorCampo(
    campoRepFiltro ? configId : null,
    campoRepFiltro?.key ?? '',
    filtrosCompletos,
  )
  // Canjes completos sin filtro de fecha: se filtra client-side en el useMemo.
  const todasCanjes = useTodasCanjesFidelizacion(campoRepFiltro ? configId : null)

  const resumen = useResumenFidelizacion(configId, filtrosCompletos)
  const porProducto = useMetricasPorProducto(configId, filtrosCompletos)

  // Categoría → conjunto de producto_id por coincidencia NO exacta de nombre. Se
  // combina (AND) con el filtro de producto explícito; ambos client-side.
  const idsCategoria = useMemo(
    () => (categoriaSel ? idsProductoDeCategoria(porProducto.data?.datos ?? [], categoriaSel) : null),
    [categoriaSel, porProducto.data],
  )
  // Producto es multi-selección (coma-separado); se aplica client-side.
  const productosSelSet = useMemo(() => {
    const base = new Set(parseMulti(filtros.producto))
    if (!idsCategoria) return base
    const combinado =
      base.size === 0 ? idsCategoria : new Set([...base].filter((id) => idsCategoria.has(id)))
    // Categoría activa sin coincidencias → no mostrar nada (centinela sin match).
    return combinado.size > 0 ? combinado : new Set(['__sin_categoria__'])
  }, [filtros.producto, idsCategoria])
  const facturasIngreso = useFacturasIngreso(configId, sinFecha ? filtrosDinamicos : filtrosCompletos)

  // Facturas para "Puntos otorgados" (client-side): filtradas por los filtros
  // dinámicos; el rango de fechas lo aplica `puntosLocales` más abajo.
  const todasFacturas = useTodasFacturasFidelizacion(configId, filtrosDinamicos)

  // Opciones de los dropdowns: Departamento/Cadena/Representante desde el listado
  // de participantes; Producto desde el desglose del periodo.
  const fieldOptions = useMemo(() => {
    const out: Record<string, FieldOption[]> = {}
    const participantes = todosParticipantes.data ?? []

    const provinciaPairs = collectPairs(participantes, (p) => p.provincia, (p) => p.provincia_nombre, true)
    if (provinciaPairs.length > 0) out.provincia = provinciaPairs

    if (campoCadenaFiltro?.key) {
      const k = campoCadenaFiltro.key
      out[k] = collectPairs(
        participantes,
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[k],
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[`${k}_nombre`],
        true,
      )
    }
    if (campoRepFiltro?.key) {
      const k = campoRepFiltro.key
      out[k] = collectPairs(
        participantes,
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[k],
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[`${k}_nombre`],
        true,
      )
    }

    const productos = porProducto.data?.datos ?? []
    if (productos.length > 0) {
      out.producto = productos
        .map((p) => ({ value: String(p.producto_id), label: p.nombre }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
    }

    // Resto de campos de texto filtrables (ej. Razón social): dropdown con los
    // valores reales del listado en vez de un input de texto libre.
    const cubiertos = [
      'provincia',
      'producto',
      ...(campoCadenaFiltro?.key ? [campoCadenaFiltro.key] : []),
      ...(campoRepFiltro?.key ? [campoRepFiltro.key] : []),
    ]
    for (const [k, opts] of Object.entries(
      autoFieldOptions(participantes, camposExternos, cubiertos),
    )) {
      if (!out[k]) out[k] = opts
    }

    // Dependiente (reporte 7): dropdown de participantes registrados por nombre.
    if (configId === 7) {
      const pares = participantePairs(participantes)
      if (pares.length > 0) out.participante_id = pares
    }
    return out
  }, [todosParticipantes.data, campoCadenaFiltro?.key, campoRepFiltro?.key, porProducto.data, camposExternos, configId])

  // Desglose por producto aplicando el filtro de producto (client-side).
  const datosPorProducto = useMemo(() => {
    const filas = porProducto.data?.datos ?? []
    return productosSelSet.size > 0
      ? filas.filter((f) => productosSelSet.has(String(f.producto_id)))
      : filas
  }, [porProducto.data, productosSelSet])

  // Embudo por representante: registrados / activos / canjearon como usuarios únicos.
  // - registrados: total histórico de participantes por rep (de todosParticipantes).
  // - activos:     participantes con ≥1 factura en el periodo seleccionado
  //                (campo `participantes` del endpoint agrupación filtrado por fecha).
  // - canjearon:   participantes únicos con ≥1 canje en el periodo (client-side
  //                desde todasCanjes, filtrado por created_at).
  const embudoData = useMemo((): EmbudoRepresentanteFila[] => {
    if (!campoRepFiltro || !todosParticipantes.data) return []
    const repKey = campoRepFiltro.key
    const repNombreKey = `${repKey}_nombre`

    // registrados: agrupar todos los participantes por rep (sin filtro de fecha).
    const registradosMap = new Map<string, number>()
    const participanteRepMap = new Map<number, string>()
    for (const p of todosParticipantes.data) {
      const extra = (p.datos_extra as Record<string, unknown>) ?? {}
      const rep = String(extra[repNombreKey] ?? extra[repKey] ?? '').trim()
      if (!rep || rep === '(sin asignar)') continue
      if (p.id) participanteRepMap.set(Number(p.id), rep)
      registradosMap.set(rep, (registradosMap.get(rep) ?? 0) + 1)
    }

    // activos: participantes únicos con ≥1 factura válida.
    // - Sin filtro de fecha (histórico): puntos_acumulados > 0 indica que
    //   el upstream otorgó puntos por al menos una factura válida.
    // - Con filtro de fecha: el endpoint de agrupación con fechas devuelve
    //   exactamente los participantes que tuvieron facturas en ese periodo.
    const activosMap = new Map<string, number>()
    const { desde, hasta } = sinFecha ? { desde: undefined, hasta: undefined } : rango
    if (sinFecha || (!desde && !hasta)) {
      for (const p of todosParticipantes.data) {
        const extra = (p.datos_extra as Record<string, unknown>) ?? {}
        const rep = String(extra[repNombreKey] ?? extra[repKey] ?? '').trim()
        if (!rep || rep === '(sin asignar)') continue
        if (parseFloat(String(p.puntos_acumulados ?? '0')) > 0) {
          activosMap.set(rep, (activosMap.get(rep) ?? 0) + 1)
        }
      }
    } else {
      for (const fila of porCampoRep.data?.datos ?? []) {
        if (typeof fila.participantes === 'number') {
          activosMap.set(fila.valor, fila.participantes)
        }
      }
    }

    // canjearon: participantes únicos con ≥1 canje en el periodo seleccionado.
    const canjeararonMap = new Map<string, Set<number>>()
    for (const c of todasCanjes.data ?? []) {
      const fechaCanje = c.created_at ? String(c.created_at).slice(0, 10) : ''
      if (desde && fechaCanje < desde) continue
      if (hasta && fechaCanje > hasta) continue
      const rep = participanteRepMap.get(Number(c.participante_id))
      if (!rep) continue
      const set = canjeararonMap.get(rep) ?? new Set<number>()
      set.add(Number(c.participante_id))
      canjeararonMap.set(rep, set)
    }

    const allReps = new Set([...registradosMap.keys(), ...activosMap.keys()])
    return Array.from(allReps)
      .map((rep): EmbudoRepresentanteFila => {
        const registrados = registradosMap.get(rep) ?? 0
        const activos = activosMap.get(rep) ?? 0
        const canjearon = canjeararonMap.get(rep)?.size ?? 0
        return {
          representante: rep,
          registrados,
          activos,
          pctActivos: registrados > 0 ? (activos / registrados) * 100 : 0,
          canjearon,
          pctCanjearon: registrados > 0 ? (canjearon / registrados) * 100 : 0,
        }
      })
      .sort((a, b) => b.registrados - a.registrados)
  }, [campoRepFiltro, todosParticipantes.data, porCampoRep.data, todasCanjes.data, sinFecha, rango])


  // Serie temporal: día a día del mes seleccionado. En reportes con lista de
  // precios local se pide en cajas equivalentes para valorarla a precio de
  // referencia; en el resto, el monto del programa (OCR) tal cual.
  // Se pasan filtrosCompletos para que los filtros dinámicos (representante,
  // jefe, cadena, etc.) afecten también a las gráficas de día a día.
  const serie = useSerieTemporal(configId, {
    granularidad: granSerie,
    metrica: usaPreciosLocales ? 'cajas_eq' : 'monto',
    ...filtrosSerie,
  })

  // Serie de registros por periodo (día/semana/mes según el toggle).
  const serieRegistros = useSerieTemporal(configId, {
    granularidad: granSerie,
    metrica: 'registros',
    ...filtrosSerie,
  })

  // Segunda serie: nº de facturas validadas por periodo (línea secundaria del chart de ventas).
  const serieFacturas = useSerieTemporal(configId, {
    granularidad: granSerie,
    metrica: 'facturas',
    ...filtrosSerie,
  })

  // Serie de registros día a día del periodo: base de la curva de participación
  // acumulada, que se cruza con `validadosPorDia` (indexado por día) y por eso se
  // mantiene siempre en granularidad diaria e independiente del toggle.
  const serieRegistrosDia = useSerieTemporal(configId, {
    granularidad: 'dia',
    metrica: 'registros',
    ...filtrosCompletos,
  })

  // Serie de participantes activos día a día: primera factura validada por
  // participante. El frontend acumula para la curva de participación (diaria).
  const serieParticipantesActivos = useSerieTemporal(configId, {
    granularidad: 'dia',
    metrica: 'participantes_activos',
    ...filtrosCompletos,
  })


  // Precio de referencia promedio ponderado del periodo (sólo reportes con lista
  // de precios local).
  const precioRefPromedio = useMemo(
    () =>
      mapaPrecios
        ? precioReferenciaPromedioPonderado(mapaPrecios, porProducto.data?.datos ?? [])
        : null,
    [mapaPrecios, porProducto.data],
  )

  // fechaData: cuando hay filtro de producto/categoría activo, se calcula
  // client-side desde todasFacturas (líneas por producto); en caso contrario
  // usa las series temporales del backend.
  const fechaData = useMemo(() => {
    const hayFiltroProducto = productosSelSet.size > 0
    if (hayFiltroProducto && todasFacturas.data) {
      const { desde, hasta } = sinFecha ? { desde: undefined, hasta: undefined } : rango
      const dia = new Map<string, number>()
      const diaFac = new Map<string, number>()
      const ESTADOS_OK = new Set(['confirmada', 'aprobada', 'validada', 'corregida', 'duplicada'])
      for (const f of todasFacturas.data) {
        const estado = String(f.estado ?? '').toLowerCase()
        if (!ESTADOS_OK.has(estado)) continue
        const fecha = toLocalDate(String(f.fecha_carga ?? f.fecha_emision ?? ''))
        if (!fecha) continue
        if (desde && fecha < desde) continue
        if (hasta && fecha > hasta) continue
        const lineas = ((f.productos as Array<Record<string, unknown>>) ?? []).filter(
          (l) => l.es_participante && productosSelSet.has(String(l.producto_id)),
        )
        if (lineas.length === 0) continue
        diaFac.set(fecha, (diaFac.get(fecha) ?? 0) + 1)
        let val = 0
        for (const l of lineas) {
          val += mapaPrecios
            ? valorProductoReferencia(mapaPrecios, { nombre: String(l.nombre ?? ''), monto: l.precio_total, cantidad_vendida: l.cantidad })
            : esValor
              ? parseNumeric(l.precio_total)
              : parseNumeric(l.cantidad)
        }
        dia.set(fecha, (dia.get(fecha) ?? 0) + val)
      }
      const fechas = Array.from(new Set([...dia.keys(), ...diaFac.keys()])).sort()
      return agruparSerieFecha(
        fechas.map((f) => ({ fecha: f, total: dia.get(f) ?? 0, secondary: diaFac.get(f) ?? 0 })),
        granSerie,
      )
    }
    // Sin filtro de producto: usa las series temporales del backend.
    const factor = precioRefPromedio != null ? precioRefPromedio : 1
    const volMap = new Map(
      (serie.data?.serie ?? []).map((p) => [p.periodo, parseNumeric(p.valor) * factor]),
    )
    const facMap = new Map(
      (serieFacturas.data?.serie ?? []).map((p) => [p.periodo, parseNumeric(p.valor)]),
    )
    const fechas = Array.from(new Set([...volMap.keys(), ...facMap.keys()])).sort()
    return fechas.map((f) => ({
      fecha: f,
      total: volMap.get(f) ?? 0,
      secondary: facMap.get(f) ?? 0,
    }))
  }, [productosSelSet, todasFacturas.data, sinFecha, rango, mapaPrecios, esValor, serie.data, serieFacturas.data, precioRefPromedio, granSerie])

  // Comprobantes recalculados client-side desde `todasFacturas` (solo reporte 7).
  // IMPRESCINDIBLE porque el endpoint upstream `metricas/facturas-ingreso` IGNORA
  // los filtros dinámicos (provincia/departamento, representante, cadena, etc.), por
  // lo que la card/tabla de comprobantes quedaban congeladas al filtrar. `todasFacturas`
  // sí viene filtrado server-side (el listado `facturas` respeta esos filtros), así
  // que lo reconstruimos aquí. Se activa con filtro de producto/categoría O con
  // cualquier filtro dinámico; sin filtros se usa el valor autoritativo del upstream
  // (la reconstrucción se trunca por el tope de páginas de `todasFacturas`).
  const facturasIngresoFiltrado = useMemo(() => {
    const hayFiltroProducto = productosSelSet.size > 0
    const hayFiltroDinamico = Object.keys(filtrosDinamicos).length > 0
    if (configId !== 7 || (!hayFiltroProducto && !hayFiltroDinamico) || !todasFacturas.data) return null
    const { desde, hasta } = sinFecha ? { desde: undefined, hasta: undefined } : rango
    const ESTADOS_OK = new Set(['confirmada', 'aprobada', 'validada', 'corregida', 'duplicada'])
    const periodoMes = rangoEfectivo.desde?.slice(0, 7) ?? null
    const porMes = new Map<string, number>()
    let totalDuplicadas = 0
    for (const f of todasFacturas.data) {
      const estado = String(f.estado ?? '').toLowerCase()
      if (!ESTADOS_OK.has(estado)) continue
      const fechaCarga = toLocalDate(String(f.fecha_carga ?? ''))
      if (desde && fechaCarga < desde) continue
      if (hasta && fechaCarga > hasta) continue
      // Con filtro de producto/categoría activo, solo cuentan comprobantes con al
      // menos una línea del participante en los productos seleccionados.
      if (hayFiltroProducto) {
        const lineas = ((f.productos as Array<Record<string, unknown>>) ?? []).filter(
          (l) => l.es_participante && productosSelSet.has(String(l.producto_id)),
        )
        if (lineas.length === 0) continue
      }
      if (estado === 'duplicada') { totalDuplicadas++; continue }
      const mesEmision = String(f.fecha_emision ?? '').slice(0, 7) || 'sin-fecha'
      porMes.set(mesEmision, (porMes.get(mesEmision) ?? 0) + 1)
    }
    const por_mes_emision = Array.from(porMes.entries())
      .map(([mes, total]) => ({
        mes_emision: mes === 'sin-fecha' ? null : mes,
        total,
        es_mismo_periodo: !!periodoMes && mes === periodoMes,
      }))
      .sort((a, b) => (a.mes_emision ?? '').localeCompare(b.mes_emision ?? ''))
    const totalValidas = por_mes_emision.reduce((s, r) => s + r.total, 0)
    return {
      total_ingresadas: totalValidas + totalDuplicadas,
      total_validas: totalValidas,
      total_duplicadas: totalDuplicadas,
      por_mes_emision,
    }
  }, [configId, productosSelSet, filtrosDinamicos, todasFacturas.data, sinFecha, rango, rangoEfectivo])

  // Tabla "Comprobantes por mes": usa el desglose mensual. El upstream
  // (`facturas-ingreso`) ignora filtros, así que se reconstruye client-side desde
  // `todasFacturas` cuando hay filtros; sin filtros, el valor del upstream.
  const usandoComprobantesLocal =
    configId === 7 && (productosSelSet.size > 0 || Object.keys(filtrosDinamicos).length > 0)
  const comprobantesData = facturasIngresoFiltrado ?? facturasIngreso.data
  const comprobantesCargando = usandoComprobantesLocal ? todasFacturas.isLoading : facturasIngreso.isLoading

  // Conteo de la card "Comprobantes de pago" (número único, debe ser rápido):
  // - Producto/categoría: el upstream no filtra por producto → reconstrucción local
  //   (sin bloquear: muestra el valor base mientras `todasFacturas` carga).
  // - Provincia/representante/ciudad/dependiente: `resumen.facturas` los honra
  //   server-side en una sola llamada rápida. (razon_social NO lo soporta el upstream.)
  // - Sin filtros: valor autoritativo de `metricas/facturas-ingreso`.
  const comprobantes = useMemo<{ value: number | undefined; loading: boolean }>(() => {
    if (configId === 7 && productosSelSet.size > 0) {
      return {
        value: facturasIngresoFiltrado?.total_ingresadas ?? facturasIngreso.data?.total_ingresadas,
        loading: facturasIngreso.isLoading,
      }
    }
    if (configId === 7 && Object.keys(filtrosDinamicos).length > 0) {
      const f = resumen.data?.facturas
      return {
        value: f ? Number(f.validadas ?? 0) + Number(f.duplicadas ?? 0) : undefined,
        loading: resumen.isLoading,
      }
    }
    return { value: facturasIngreso.data?.total_ingresadas, loading: facturasIngreso.isLoading }
  }, [configId, productosSelSet, filtrosDinamicos, facturasIngresoFiltrado, resumen.data, resumen.isLoading, facturasIngreso.data, facturasIngreso.isLoading])

  // Resumen Consumo/Farma siempre visible (solo reporte 7), independiente del filtro activo.
  // Cada comprobante se asigna a UNA sola categoría (Consumo > Farma; sin match → Consumo)
  // para que consumo + farma === total de la tabla.
  const consumoFarmaResumen = useMemo(() => {
    if (configId !== 7 || !todasFacturas.data || !porProducto.data) return null
    const { desde, hasta } = sinFecha ? { desde: undefined, hasta: undefined } : rango
    const ESTADOS_OK = new Set(['confirmada', 'aprobada', 'validada', 'corregida', 'duplicada'])
    const idsFarma = idsProductoDeCategoria(porProducto.data.datos ?? [], 'Farma')
    let farma = 0, total = 0
    for (const f of todasFacturas.data) {
      const estado = String(f.estado ?? '').toLowerCase()
      if (!ESTADOS_OK.has(estado)) continue
      const fechaCarga = toLocalDate(String(f.fecha_carga ?? ''))
      if (desde && fechaCarga < desde) continue
      if (hasta && fechaCarga > hasta) continue
      total++
      const lineas = (f.productos as Array<Record<string, unknown>>) ?? []
      const tieneFarma = lineas.some((l) => l.es_participante && idsFarma.has(String(l.producto_id)))
      if (tieneFarma) farma++
    }
    // consumo se deriva del total para que consumo + farma === total exacto
    return { farma, totalLocal: total }
  }, [configId, todasFacturas.data, porProducto.data, sinFecha, rango])

  // Total $ del mes: suma de la serie valorada.
  const totalMontoMes = useMemo(
    () => fechaData.reduce((acc, p) => acc + p.total, 0),
    [fechaData],
  )

  // Volumen total del periodo: $ vendido (modo valor) o cajas eq. (modo cantidad).
  const totalVolumen = useMemo(() => {
    return datosPorProducto.reduce(
      (acc, f) => acc + parseNumeric(esValor ? f.monto : f.cantidad_vendida),
      0,
    )
  }, [datosPorProducto, esValor])

  const registrosFechaData = useMemo(
    () =>
      (serieRegistros.data?.serie ?? []).map((p) => ({
        fecha: p.periodo,
        total: parseNumeric(p.valor),
      })),
    [serieRegistros.data],
  )

  // Validados = email_verificado, mismo criterio que el KPI de participantes.
  // Se construye como mapa día→count para la serie acumulada del chart.
  const validadosPorDia = useMemo(() => {
    const participantes = todosParticipantes.data ?? []
    const m = new Map<string, number>()
    for (const p of participantes) {
      const extra = (p.datos_extra ?? {}) as Record<string, unknown>
      const v = extra.email_verificado ?? (p as Record<string, unknown>).email_verificado
      const esVal = v === true || v === 'true' || v === 1 || v === '1'
      if (!esVal) continue
      const createdAt = (p as Record<string, unknown>).created_at
      if (typeof createdAt !== 'string' || createdAt.length < 10) continue
      const dia = toLocalDate(createdAt)
      m.set(dia, (m.get(dia) ?? 0) + 1)
    }
    return m
  }, [todosParticipantes.data])

  const validadosEmailCount = useMemo(
    () => Array.from(validadosPorDia.values()).reduce((s, n) => s + n, 0),
    [validadosPorDia],
  )

  // Curva acumulada de participación: registros acumulados (primary) vs validados
  // acumulados (tertiary) vs activos acumulados (secondary).
  const participacionAcumData = useMemo(() => {
    // Se agrupan las tres series diarias por la misma clave de bucket
    // (día/semana/mes) para que la curva acumulada quede alineada en cualquier
    // granularidad, sin depender del formato de periodo del backend.
    const acumularPorClave = (serie: Array<{ periodo: string; valor: number | string }>) => {
      const m = new Map<string, number>()
      for (const p of serie) {
        const k = claveGranularidad(p.periodo, granSerie)
        m.set(k, (m.get(k) ?? 0) + parseNumeric(p.valor))
      }
      return m
    }
    const regMap = acumularPorClave(serieRegistrosDia.data?.serie ?? [])
    const actMap = acumularPorClave(serieParticipantesActivos.data?.serie ?? [])
    const valMap = new Map<string, number>()
    for (const [dia, n] of validadosPorDia) {
      const k = claveGranularidad(dia, granSerie)
      valMap.set(k, (valMap.get(k) ?? 0) + n)
    }
    const fechas = Array.from(new Set([...regMap.keys(), ...actMap.keys(), ...valMap.keys()])).sort()
    const totalValidados = validadosEmailCount > 0 ? validadosEmailCount : (resumen.data?.participantes?.validados ?? 0)
    let acumReg = 0
    let acumAct = 0
    let acumVal = 0
    return fechas.map((f) => {
      acumReg += regMap.get(f) ?? 0
      acumAct += actMap.get(f) ?? 0
      acumVal += valMap.get(f) ?? 0
      const percent = totalValidados > 0 ? (acumAct / totalValidados) * 100 : 0
      return { fecha: f, total: acumReg, tertiary: acumVal, secondary: acumAct, percent, validados: acumVal }
    })
  }, [serieRegistrosDia.data, serieParticipantesActivos.data, validadosPorDia, validadosEmailCount, resumen.data, granSerie])

  const totalRegistrosPeriodo = useMemo(
    () => registrosFechaData.reduce((acc, p) => acc + p.total, 0),
    [registrosFechaData],
  )

  // Participación por periodo (solo semana/mes), indexada por clave de bucket:
  // registros totales, activos y % (activos / totales). Se agrupan las series
  // DIARIAS por la misma clave que el tooltip normaliza, para que el dato esté
  // disponible en todas las gráficas de la sección independientemente del formato
  // de periodo que use cada una.
  const participacionPorClave = useMemo(() => {
    const m = new Map<string, { total: number; activos: number; pct: number }>()
    if (granSerie === 'dia') return m
    const acumular = (serie: Array<{ periodo: string; valor: number | string }>, campo: 'total' | 'activos') => {
      for (const p of serie) {
        const k = claveGranularidad(p.periodo, granSerie)
        const e = m.get(k) ?? { total: 0, activos: 0, pct: 0 }
        e[campo] += parseNumeric(p.valor)
        m.set(k, e)
      }
    }
    acumular(serieRegistrosDia.data?.serie ?? [], 'total')
    acumular(serieParticipantesActivos.data?.serie ?? [], 'activos')
    for (const e of m.values()) e.pct = e.total > 0 ? (e.activos / e.total) * 100 : 0
    return m
  }, [granSerie, serieRegistrosDia.data, serieParticipantesActivos.data])

  // Tooltip de % participación reutilizable por todas las gráficas de la sección.
  // Normaliza la fecha del punto a la clave de bucket (idempotente) y busca en el
  // mapa. `undefined` en diario → las gráficas no muestran el bloque extra.
  const tooltipParticipacion = granSerie === 'dia'
    ? undefined
    : (punto: Record<string, unknown>) => {
        const e = participacionPorClave.get(claveGranularidad(String(punto.fecha ?? ''), granSerie))
        if (!e) return null
        return (
          <span className="mt-1 flex flex-col gap-0.5 border-t border-border pt-1 text-[11px]">
            <span className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Registros del periodo</span>
              <span className="font-medium tabular-nums text-foreground">{formatInteger(e.total, configId)}</span>
            </span>
            <span className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">Activos del periodo</span>
              <span className="font-medium tabular-nums text-foreground">{formatInteger(e.activos, configId)}</span>
            </span>
            <span className="flex items-center justify-between gap-4">
              <span className="text-muted-foreground">% participación</span>
              <span className="font-semibold tabular-nums text-foreground">{formatDecimal(e.pct, configId, 1)}%</span>
            </span>
          </span>
        )
      }

  // Promedios diarios: mismo criterio que Ventas (días con facturas > 0 como divisor).
  const promedioDiario = useMemo(() => {
    const diasConFacturas = fechaData.filter((d) => d.secondary > 0)
    const dias = diasConFacturas.length
    const diasConRegistros = registrosFechaData.filter((d) => d.total > 0)
    return {
      ventas: dias > 0 ? diasConFacturas.reduce((s, d) => s + d.total, 0) / dias : 0,
      facturas: dias > 0 ? diasConFacturas.reduce((s, d) => s + d.secondary, 0) / dias : 0,
      registros: diasConRegistros.length > 0
        ? diasConRegistros.reduce((s, d) => s + d.total, 0) / diasConRegistros.length
        : 0,
    }
  }, [fechaData, registrosFechaData])

  const color = normalizeColor(config.data)
  const cargandoBase = config.isLoading || metadata.isLoading
  const errorGlobal = config.error || metadata.error || resumen.error

  const handleRefresh = () => {
    metadata.refetch()
    resumen.refetch()
    porProducto.refetch()
    serie.refetch()
    serieFacturas.refetch()
    serieRegistros.refetch()
    serieRegistrosDia.refetch()
    serieParticipantesActivos.refetch()
    todasFacturas.refetch()
    facturasIngreso.refetch()
    porCampoRep.refetch()
    todosParticipantes.refetch()
    todasCanjes.refetch()
  }

  return (
    <div className="space-y-6">
      <ReporteHeader
        config={config.data}
        programaNombre={metadata.data?.programa.nombre}
        icon={Trophy}
        seccion="Resumen Gerencial"
        onRefresh={handleRefresh}
        refreshing={
          metadata.isFetching ||
          resumen.isFetching ||
          porProducto.isFetching ||
          serie.isFetching
        }
      />

      <ReporteSubNav
        configId={configId}
        colorNombre={color.nombre}
        colorTono={color.tono}
      />

      <ReportePeriodoBar
        rango={rango}
        mes={mes}
        onMesChange={setMes}
        minDate={metadata.data?.rango_datos?.primera_factura ?? null}
        maxDate={metadata.data?.rango_datos?.ultima_factura ?? null}
        configId={configId}
        colorNombre={color.nombre}
        exportTipos={[
          { tipo: 'metricas-resumen', label: 'Resumen del periodo' },
        ]}
        exportFiltros={{}}
        sinFecha={sinFecha}
        onSinFechaChange={setSinFecha}
        modo={modo}
        onModoChange={setModo}
        rangoPersonalizado={rangoPersonalizado}
        onRangoPersonalizadoChange={setRangoPersonalizado}
      />

      {/* Filtros de la sección (reporte 7): fuerza de ventas, departamento, cadena, producto */}
      {configId === 7 && (
        <DynamicFiltersBar
          configId={configId}
          metadata={metadata.data}
          value={filtros}
          onChange={setFiltros}
          hideDateRange
          showCategoria
          omitDynamicFields={['provincia', 'nivel', 'email_verificado', 'activo']}
          fieldOptions={narrowRepOptions(fieldOptions)}
          cascadeSelects={cascadeSelects}
        />
      )}

      {errorGlobal && (
        <Alert variant="destructive">
          <AlertDescription>{parseFidelizacionError(errorGlobal).message}</AlertDescription>
        </Alert>
      )}

      {cargandoBase || resumen.isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      ) : resumen.data ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              icon={Users}
              label="Participantes"
              value={formatInteger(resumen.data.participantes.total, configId)}
              hint={(() => {
                const v = validadosEmailCount > 0 ? validadosEmailCount : resumen.data.participantes.validados
                const p = Math.max(resumen.data.participantes.total - v, 0)
                return `${formatInteger(v, configId)} validados · ${formatInteger(p, configId)} pendientes`
              })()}
              colorNombre={color.nombre}
              colorTono={color.tono}
              gradient
            />
            <KpiCard
              icon={FileText}
              label={terminoComprobante(configId, { plural: true, capitalize: true })}
              value={comprobantes.loading ? '—' : formatInteger(comprobantes.value ?? resumen.data.facturas.validadas, configId)}
              hint={(() => {
                const n = comprobantes.value ?? resumen.data.facturas.validadas
                return `${formatMoneda(costoUnitarioFactura, configId)} c/u = ${formatMoneda(n * costoUnitarioFactura, configId)}`
              })()}
              colorNombre={color.nombre}
              colorTono={color.tono}
              gradient
            />
            <KpiCard
              icon={Package}
              label={esValor ? 'Ventas Totales' : 'Unidades Vendidas en Caja'}
              value={
                porProducto.isLoading
                  ? '—'
                  : esValor
                    ? formatMoneda(totalVolumen, configId)
                    : formatDecimal(totalVolumen, configId, 2)
              }
              hint={esValor ? 'Monto vendido en el periodo' : 'Cajas equivalentes normalizadas'}
              colorNombre={color.nombre}
              colorTono={color.tono}
              gradient
            />
            <KpiCard
              icon={Gift}
              label="Puntos canjeados"
              value={formatDecimal(resumen.data.puntos.canjeados, configId)}
              hint={`${formatMoneda(resumen.data.canjes.monto_total, configId)} en canjes`}
              colorNombre={color.nombre}
              colorTono={color.tono}
              gradient
            />
          </div>

          {/* Facturas ingresadas en el periodo — desglose por mes de fecha_emision */}
          {(() => {
            const dataTabla = comprobantesData
            const cargandoTabla = comprobantesCargando
            const etiquetaFiltro = categoriaSel ?? (productosSelSet.size > 0 ? 'filtrado' : null)
            return (
          <Card className="border border-gray-300 dark:border-gray-700">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Upload className="h-4 w-4 text-muted-foreground" />
                  {esPeru(configId) ? 'Comprobantes de pago ingresados en el período' : 'Facturas ingresadas en el período'}
                  {etiquetaFiltro && (
                    <span className="text-xs font-normal text-muted-foreground border border-gray-300 dark:border-gray-600 rounded px-1.5 py-0.5">
                      {etiquetaFiltro}
                    </span>
                  )}
                </CardTitle>
                {dataTabla && (
                  <span className="inline-flex flex-col items-end leading-tight rounded-md border border-gray-200 dark:border-gray-700 bg-muted/40 px-2 py-1 text-xs">
                    <span className="font-semibold text-black dark:text-white">
                      {formatInteger(dataTabla.total_ingresadas, configId)}
                    </span>
                    <span className="text-muted-foreground text-[10px]">confirmadas/aprobadas/corregidas + duplicadas</span>
                  </span>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                {cargandoTabla ? (
                  <Skeleton className="h-24 w-full" />
                ) : facturasIngreso.error && !facturasIngresoFiltrado ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {parseFidelizacionError(facturasIngreso.error).message}
                  </p>
                ) : dataTabla && dataTabla.por_mes_emision.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700 text-muted-foreground text-xs">
                          <th className="text-left py-2 pr-4 font-medium">Mes emisión</th>
                          <th className="text-right py-2 px-4 font-medium">{terminoComprobante(configId, { plural: true, capitalize: true })}</th>
                          <th className="text-left py-2 pl-2 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {dataTabla.por_mes_emision.map((fila) => (
                          <tr
                            key={fila.mes_emision ?? 'null'}
                            className="border-b border-gray-100 dark:border-gray-800 last:border-0"
                          >
                            <td className="py-1.5 pr-4 font-mono text-xs">
                              {fila.mes_emision ?? <span className="text-muted-foreground italic">sin fecha</span>}
                            </td>
                            <td className="py-1.5 px-4 text-right tabular-nums font-medium">
                              {formatInteger(fila.total, configId)}
                            </td>
                            <td className="py-1.5 pl-2">
                              {fila.es_mismo_periodo ? (
                                <span className="text-xs text-green-600 dark:text-green-400 font-medium">✓ mismo mes</span>
                              ) : (
                                <span className="text-xs text-amber-600 dark:text-amber-400">otro mes</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {dataTabla.total_duplicadas > 0 && (
                          <tr className="border-b border-gray-100 dark:border-gray-800 text-muted-foreground">
                            <td className="py-1.5 pr-4 text-xs italic">Duplicadas</td>
                            <td className="py-1.5 px-4 text-right tabular-nums text-xs">
                              {formatInteger(dataTabla.total_duplicadas, configId)}
                            </td>
                            <td className="py-1.5 pl-2" />
                          </tr>
                        )}
                        <tr className="border-t-2 border-gray-300 dark:border-gray-600">
                          <td className="pt-2 pr-4 text-xs font-semibold">Total</td>
                          <td className="pt-2 px-4 text-right tabular-nums font-bold">
                            {formatInteger(dataTabla.total_ingresadas, configId)}
                          </td>
                          <td className="pt-2 pl-2" />
                        </tr>
                      </tbody>
                    </table>
                    {dataTabla.por_mes_emision.some(f => !f.es_mismo_periodo) && (
                      <p className="text-xs text-muted-foreground mt-3">
                        {esPeru(configId)
                          ? 'Los comprobantes de pago con "otro mes" fueron subidos en este período pero tienen fecha de emisión de otro mes — aparecen en el reporte de ese mes.'
                          : 'Las facturas con "otro mes" fueron subidas en este período pero tienen fecha de emisión de otro mes — aparecen en el reporte de ese mes.'}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">{esPeru(configId) ? 'Sin comprobantes de pago ingresados en este período.' : 'Sin facturas ingresadas en este período.'}</p>
                )}
                {esPeru(configId) && todasFacturas.isLoading && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                    <div className="flex gap-3">
                      <Skeleton className="flex-1 h-10 rounded-md" />
                      <Skeleton className="flex-1 h-10 rounded-md" />
                    </div>
                    <div className="flex gap-3">
                      <Skeleton className="flex-1 h-10 rounded-md" />
                      <Skeleton className="flex-1 h-10 rounded-md" />
                    </div>
                  </div>
                )}
                {consumoFarmaResumen && (() => {
                  const totalValidas = (dataTabla?.total_ingresadas ?? consumoFarmaResumen.totalLocal) - (dataTabla?.total_duplicadas ?? 0)
                  const consumo = totalValidas - consumoFarmaResumen.farma
                  const pctF = totalValidas > 0 ? consumoFarmaResumen.farma / totalValidas : 0
                  const pctC = totalValidas > 0 ? consumo / totalValidas : 0
                  const dups = dataTabla?.total_duplicadas ?? 0
                  const dupC = Math.round(dups * pctC)
                  const dupF = dups - dupC
                  const cardCls = 'flex-1 rounded-md border border-gray-200 dark:border-gray-700 bg-muted/30 px-4 py-2.5'
                  return (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                      <div className="flex gap-3">
                        <div className={`${cardCls} flex items-center justify-between`}>
                          <span className="text-xs text-muted-foreground font-medium">Consumo</span>
                          <div className="flex items-baseline gap-2">
                            <span className="text-[10px] text-muted-foreground tabular-nums">{(pctC * 100).toFixed(1)}%</span>
                            <span className="text-base font-bold tabular-nums text-foreground">{formatInteger(consumo, configId)}</span>
                          </div>
                        </div>
                        <div className={`${cardCls} flex items-center justify-between`}>
                          <span className="text-xs text-muted-foreground font-medium">Farma</span>
                          <div className="flex items-baseline gap-2">
                            <span className="text-[10px] text-muted-foreground tabular-nums">{(pctF * 100).toFixed(1)}%</span>
                            <span className="text-base font-bold tabular-nums text-foreground">{formatInteger(consumoFarmaResumen.farma, configId)}</span>
                          </div>
                        </div>
                      </div>
                      {dups > 0 && (
                        <div className="flex gap-3">
                          <div className={`${cardCls} flex items-center justify-between`}>
                            <span className="text-xs text-muted-foreground font-medium">Duplicadas Consumo</span>
                            <div className="flex items-baseline gap-2">
                              <span className="text-[10px] text-muted-foreground tabular-nums">{(pctC * 100).toFixed(1)}%</span>
                              <span className="text-base font-bold tabular-nums text-foreground">{formatInteger(dupC, configId)}</span>
                            </div>
                          </div>
                          <div className={`${cardCls} flex items-center justify-between`}>
                            <span className="text-xs text-muted-foreground font-medium">Duplicadas Farma</span>
                            <div className="flex items-baseline gap-2">
                              <span className="text-[10px] text-muted-foreground tabular-nums">{(pctF * 100).toFixed(1)}%</span>
                              <span className="text-base font-bold tabular-nums text-foreground">{formatInteger(dupF, configId)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })()}
              </CardContent>
            </Card>
            )
          })()}

          <AreaChartFecha
            title={`Registros — ${GRAN_RITMO[granSerie]}`}
            data={registrosFechaData}
            colorNombre={color.nombre}
            colorTono={color.tono}
            isLoading={serieRegistros.isLoading}
            icon={CalendarDays}
            primaryLabel="Registros"
            headerActions={<GranularidadToggle value={granSerie} onChange={setGranSerie} />}
            granularidad={granSerie}
            tooltipExtra={tooltipParticipacion}
            stats={
              totalRegistrosPeriodo > 0
                ? [
                    { label: esAmpliado ? 'total histórico' : 'total periodo', value: formatInteger(totalRegistrosPeriodo, configId), series: 'primary' },
                    { label: `registros/${GRAN_SUFIJO[granSerie]}`, value: serieRegistros.isLoading ? '—' : formatDecimal(promedioDiario.registros, configId, 1), series: 'primary' },
                  ]
                : undefined
            }
            formatValue={(v) => formatNumero(v, configId)}
            configId={configId}
          />

          <AreaChartFecha
            title={`Monto del programa — ${GRAN_RITMO[granSerie]}`}
            data={fechaData}
            colorNombre={color.nombre}
            colorTono={color.tono}
            isLoading={serie.isLoading || serieFacturas.isLoading}
            icon={TrendingUp}
            primaryLabel="Ventas S/"
            secondaryLabel={`Cantidad de ${terminoComprobante(configId, { plural: true })}`}
            formatValue={(v) => formatMoneda(v, configId, { maximumFractionDigits: 0 })}
            secondaryFormatValue={(v) => formatDecimal(v, configId, 0)}
            headerActions={<GranularidadToggle value={granSerie} onChange={setGranSerie} />}
            granularidad={granSerie}
            tooltipExtra={tooltipParticipacion}
            stats={[
              {
                label: esAmpliado ? 'total histórico' : 'total mes',
                value: serie.isLoading ? '—' : formatMoneda(totalMontoMes, configId, { maximumFractionDigits: 0 }),
                series: 'primary',
              },
              {
                label: `ventas/${GRAN_SUFIJO[granSerie]}`,
                value: serie.isLoading || serieFacturas.isLoading ? '—' : formatMoneda(promedioDiario.ventas, configId, { maximumFractionDigits: 0 }),
                series: 'primary',
              },
              {
                label: `${terminoComprobante(configId, { plural: true })}/${GRAN_SUFIJO[granSerie]}`,
                value: serieFacturas.isLoading ? '—' : formatDecimal(promedioDiario.facturas, configId, 1),
                series: 'secondary',
              },
            ]}
            configId={configId}
          />

          {/* Curva de participación acumulada: registros vs validados vs activos */}
          <AreaChartFecha
            title="Participación acumulada — registros vs validados vs activos"
            data={participacionAcumData}
            showPercentBar
            colorNombre={color.nombre}
            colorTono={color.tono}
            isLoading={serieRegistrosDia.isLoading || serieParticipantesActivos.isLoading || todosParticipantes.isLoading}
            icon={Users}
            headerActions={<GranularidadToggle value={granSerie} onChange={setGranSerie} />}
            granularidad={granSerie}
            tooltipExtra={tooltipParticipacion}
            primaryLabel="Registros acumulados"
            tertiaryLabel="Validados acumulados"
            secondaryLabel="Activos acumulados"
            formatValue={(v) => formatNumero(Math.round(v), configId)}
            tertiaryFormatValue={(v) => formatNumero(Math.round(v), configId)}
            secondaryFormatValue={(v) => formatNumero(Math.round(v), configId)}
            stats={(() => {
              const totalReg = participacionAcumData.at(-1)?.total ?? 0
              const totalVal = participacionAcumData.at(-1)?.tertiary ?? 0
              const totalAct = participacionAcumData.at(-1)?.secondary ?? 0
              const pct = participacionAcumData.at(-1)?.percent ?? 0
              return [
                { label: 'registros', value: serieRegistros.isLoading ? '—' : formatInteger(totalReg, configId), series: 'primary' as const },
                { label: 'validados', value: todosParticipantes.isLoading ? '—' : formatInteger(totalVal, configId), series: 'primary' as const },
                { label: 'activos', value: serieParticipantesActivos.isLoading ? '—' : formatInteger(totalAct, configId), series: 'secondary' as const },
                { label: '% participación', value: serieParticipantesActivos.isLoading ? '—' : `${formatDecimal(pct, configId, 1)}%`, series: 'secondary' as const },
              ]
            })()}
            configId={configId}
          />

          {campoRepFiltro && (
            <TablaEmbudoRepresentante
              data={embudoData}
              isLoading={todosParticipantes.isLoading || porCampoRep.isLoading || todasCanjes.isLoading}
              programaNombre={metadata.data?.programa.nombre}
            />
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-8">
          Sin datos para el mes seleccionado.
        </p>
      )}
    </div>
  )
}
